// src/hooks/useLocationSelection.ts

import { useState, useEffect, useMemo } from "react";
import {
  getCountriesList,
  getStatesListByCountry,
  getCitiesListByState,
  SelectOption, // Import the SelectOption interface
} from "../../Service/CommonService";

interface LocationInitialValues {
  countryCode?: string;
  stateCode?: string;
  cityName?: string;
}

interface UseLocationSelectionReturn {
  countryOptions: SelectOption[];
  stateOptions: SelectOption[];
  cityOptions: SelectOption[];
  selectedCountry: SelectOption | null;
  selectedState: SelectOption | null;
  selectedCity: SelectOption | null;
  handleCountryChange: (selectedOption: SelectOption | null) => void;
  handleStateChange: (selectedOption: SelectOption | null) => void;
  handleCityChange: (selectedOption: SelectOption | null) => void;
  selectedCountryCode: string;
  selectedStateCode: string;
  selectedCityName: string;
  setSelectedCountryCode: React.Dispatch<React.SetStateAction<string>>;
  setSelectedStateCode: React.Dispatch<React.SetStateAction<string>>;
  setSelectedCityName: React.Dispatch<React.SetStateAction<string>>;
}

export const useLocationSelection = (
  initialValues: LocationInitialValues = {}
): UseLocationSelectionReturn => {
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    initialValues.countryCode || ""
  );
  const [selectedStateCode, setSelectedStateCode] = useState<string>(initialValues.stateCode || "");
  const [selectedCityName, setSelectedCityName] = useState<string>(initialValues.cityName || "");

  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [stateOptions, setStateOptions] = useState<SelectOption[]>([]);
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);

  // Fetch countries on component mount
  useEffect(() => {
    setCountryOptions(getCountriesList());
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (selectedCountryCode) {
      setStateOptions(getStatesListByCountry(selectedCountryCode));
      setSelectedStateCode(""); // Reset state when country changes
      setSelectedCityName(""); // Reset city when country changes
    } else {
      setStateOptions([]);
      setSelectedStateCode("");
      setSelectedCityName("");
    }
  }, [selectedCountryCode]);

  // Fetch cities when state changes
  useEffect(() => {
    if (selectedCountryCode && selectedStateCode) {
      setCityOptions(getCitiesListByState(selectedCountryCode, selectedStateCode));
      setSelectedCityName(""); // Reset city when state changes
    } else {
      setCityOptions([]);
      setSelectedCityName("");
    }
  }, [selectedCountryCode, selectedStateCode]);

  // --- Handlers for dropdown changes ---
  const handleCountryChange = (selectedOption: SelectOption | null) => {
    setSelectedCountryCode(selectedOption ? selectedOption.value : "");
  };

  const handleStateChange = (selectedOption: SelectOption | null) => {
    setSelectedStateCode(selectedOption ? selectedOption.value : "");
  };

  const handleCityChange = (selectedOption: SelectOption | null) => {
    setSelectedCityName(selectedOption ? selectedOption.value : "");
  };

  // --- Memoized selected values for React-Select initial rendering ---
  const selectedCountry = useMemo(() => {
    return countryOptions.find((opt) => opt.value === selectedCountryCode) || null;
  }, [countryOptions, selectedCountryCode]);

  const selectedState = useMemo(() => {
    return stateOptions.find((opt) => opt.value === selectedStateCode) || null;
  }, [stateOptions, selectedStateCode]);

  const selectedCity = useMemo(() => {
    return cityOptions.find((opt) => opt.value === selectedCityName) || null;
  }, [cityOptions, selectedCityName]);

  return {
    countryOptions,
    stateOptions,
    cityOptions,
    selectedCountry,
    selectedState,
    selectedCity,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
    selectedCountryCode,
    selectedStateCode,
    selectedCityName,
    setSelectedCountryCode,
    setSelectedStateCode,
    setSelectedCityName,
  };
};
